import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Minus, Plus, ShoppingCart, TrendingDown, Check, Clock, AlertTriangle } from 'lucide-react';
import { fmt, tierFor, nextTier } from './promoCart';
import { OfferMedia, Thumbs, galleryItems } from './OfferMedia';
import { TierPills, KeySpecs, DatasheetButton, TrustRow, UnitPrice } from './OfferCardParts';
import { OfferDocuments } from './OfferDocuments';

const GOLD = 'text-[#C9A84C]';

export const QtySelector = ({ value, onChange, max, testid }) => (
  <div className="flex items-center bg-zinc-900 rounded-xl border border-zinc-700 shrink-0" data-testid={testid}>
    <button type="button" onClick={() => onChange(Math.max(1, value - 1))} aria-label="Moins" className="w-11 h-11 flex items-center justify-center text-zinc-300 hover:text-white" data-testid={`${testid}-minus`}><Minus className="w-4 h-4" /></button>
    <input type="number" inputMode="numeric" min={1} max={max || undefined} value={value}
      onChange={(e) => onChange(Math.max(1, Math.min(max || 99999, parseInt(e.target.value, 10) || 1)))}
      className="w-12 h-11 bg-transparent text-center text-[#F2EFE8] font-bold outline-none [appearance:textfield]" data-testid={`${testid}-input`} />
    <button type="button" onClick={() => onChange(max ? Math.min(max, value + 1) : value + 1)} aria-label="Plus" className="w-11 h-11 flex items-center justify-center text-zinc-300 hover:text-white" data-testid={`${testid}-plus`}><Plus className="w-4 h-4" /></button>
  </div>
);

export const TierTable = ({ offer, activeMin, slug }) => (
  <table className="w-full text-[13px] md:text-xs" data-testid={`tier-table-${slug}`}>
    <thead>
      <tr className="text-zinc-500 text-[11px] uppercase tracking-wider">
        <th className="text-left font-medium pb-1">Quantité</th>
        <th className="text-right font-medium pb-1">HT / pce</th>
        <th className="text-right font-medium pb-1">TTC / pce</th>
      </tr>
    </thead>
    <tbody>
      {offer.tiers.map((t) => (
        <tr key={t.min} className={`border-t border-zinc-800/80 transition-colors ${t.min === activeMin ? `${GOLD} font-semibold bg-[#C9A84C]/[0.06]` : 'text-zinc-300'}`} data-testid={`tier-row-${slug}-${t.min}`}>
          <td className="py-1 md:py-[3px] pl-2">{t.max ? `${t.min} à ${t.max} pces` : `dès ${t.min} pces`}</td>
          <td className="py-1 md:py-[3px] text-right tabular-nums">CHF {fmt(t.price_ht)}</td>
          <td className="py-1 md:py-[3px] text-right tabular-nums pr-2">CHF {fmt(t.price_ttc)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SavingHint = ({ offer, totalQty, slug }) => {
  const cur = tierFor(offer, totalQty);
  const next = nextTier(offer, totalQty);
  if (!next) return <p className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid={`saving-hint-${slug}`}><Check className="w-3.5 h-3.5" /> Meilleur prix atteint : CHF {fmt(cur.price_ht)} HT / pce</p>;
  const missing = next.min - totalQty;
  return (
    <p className="text-xs text-zinc-400 flex items-start gap-1.5" data-testid={`saving-hint-${slug}`}>
      <TrendingDown className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
      <span>Encore <strong className="text-[#F2EFE8]">{missing} pce{missing > 1 ? 's' : ''}</strong> et toutes vos pièces passent à CHF {fmt(next.price_ht)} HT : <strong className="text-emerald-400">CHF {fmt(cur.price_ht - next.price_ht)} d'économie par pièce</strong>.</span>
    </p>
  );
};

const useCountdown = (endDate) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endDate) return undefined;
    const tick = () => setLeft(Math.max(0, new Date(endDate).getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);
  return left;
};

const Countdown = ({ endDate, slug }) => {
  const ms = useCountdown(endDate);
  if (!endDate || ms === null || ms <= 0) return null;
  const parts = [[Math.floor(ms / 86400000), 'j'], [Math.floor((ms % 86400000) / 3600000), 'h'], [Math.floor((ms % 3600000) / 60000), 'min'], [Math.floor((ms % 60000) / 1000), 's']];
  return (
    <p className="text-xs text-zinc-300 flex items-center gap-2" data-testid={`offer-countdown-${slug}`}>
      <Clock className={`w-3.5 h-3.5 ${GOLD}`} /> Fin de l'offre dans
      <span className="font-mono tabular-nums text-[#F2EFE8]">{parts.map(([v, u]) => `${String(v).padStart(2, '0')}${u}`).join(' ')}</span>
    </p>
  );
};

export const isLowStock = (offer) => !offer.sold_out && offer.stock < (offer.low_stock_threshold ?? 50);


export const QuestionLink = ({ subject, testid }) => (
  <a href={`mailto:info@ledzone.ch?subject=${encodeURIComponent(subject)}`} data-testid={testid}
    className="inline-flex items-center min-h-[44px] md:min-h-0 text-xs text-zinc-500 hover:text-[#C9A84C] underline-offset-4 hover:underline transition-colors self-start">
    Une question ? Écrivez-nous
  </a>
);

const isWarranty = (f) => /garantie/i.test(f);

const OfferTypeInfo = ({ offer }) => {
  const t = offer.offer_type;
  const chips = [];
  if (t === 'percent' && offer.discount_percent) chips.push(`-${offer.discount_percent} % sur le prix de référence CHF ${fmt(offer.reference_price_ht)} HT`);
  if (t === 'bxgy' && offer.buy_x) chips.push(`${offer.buy_x} achetée${offer.buy_x > 1 ? 's' : ''} = ${offer.get_y} offerte${offer.get_y > 1 ? 's' : ''}`);
  if (offer.free_shipping) chips.push('Livraison offerte');
  if ((offer.min_qty || 1) > 1 || offer.max_qty) chips.push(`Commande : ${offer.min_qty || 1} à ${offer.max_qty || '∞'} ${t === 'pack' ? 'lot(s)' : 'pce(s)'}`);
  if (!chips.length && !(t === 'pack' && offer.pack_items?.length)) return null;
  return (
    <div className="space-y-1" data-testid={`offer-type-info-${offer.slug}`}>
      {t === 'pack' && offer.pack_items?.length > 0 && <p className="text-xs text-zinc-300">Le lot contient : {offer.pack_items.join(' + ')}</p>}
      {chips.length > 0 && <div className="flex flex-wrap gap-1.5">{chips.map((c) => <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{c}</span>)}</div>}
    </div>
  );
};

// Pastilles sur 2 lignes maximum ; « + X » déplie le reste
const FeaturePills = ({ offer }) => {
  const all = offer.features || [];
  const items = [...all.filter(isWarranty), ...all.filter((f) => !isWarranty(f))];
  const ref = useRef(null);
  const [hidden, setHidden] = useState(0);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return undefined;
    const measure = () => {
      const lis = [...el.querySelectorAll('li[data-pill]')];
      lis.forEach((li) => { li.style.display = ''; });
      const tops = [...new Set(lis.map((li) => li.offsetTop))].sort((a, b) => a - b);
      const limit = tops[1] ?? tops[0];
      let n = 0;
      lis.forEach((li) => { if (li.offsetTop > limit) { li.style.display = 'none'; n += 1; } });
      if (n > 0) {
        const visible = lis.filter((li) => li.style.display !== 'none');
        const more = el.querySelector('[data-more]');
        while (more && visible.length && more.offsetTop > limit) { visible.pop().style.display = 'none'; n += 1; }
      }
      setHidden(n);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [items.join('|'), open]);
  if (!items.length) return null;
  return (
    <ul ref={ref} className="flex flex-wrap gap-1.5" data-testid={`offer-features-${offer.slug}`}>
      {items.map((f) => (
        <li key={f} data-pill style={open ? { display: '' } : undefined}
          className={`text-[11px] md:text-xs px-2.5 py-1 rounded-full border ${isWarranty(f) ? 'text-[#E6CF8A] border-[#C9A84C]/50 bg-[#C9A84C]/10' : 'text-zinc-300 border-white/10 bg-white/[0.03]'}`}
          data-testid={isWarranty(f) ? `offer-warranty-${offer.slug}` : undefined}>{f}</li>
      ))}
      {(hidden > 0 || open) && (
        <li data-more>
          <button type="button" onClick={() => setOpen(!open)} data-testid={`offer-features-more-${offer.slug}`}
            className="text-[11px] md:text-xs px-2.5 py-1 rounded-full border border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10">
            {open ? 'Réduire' : `+ ${hidden}`}
          </button>
        </li>
      )}
    </ul>
  );
};

export const PromoOfferCard = ({ offer, cart, qty, setQty, highlighted, onAdded, onEdit, onRequest, eager, index = 0 }) => {
  const inCart = cart.qtyOf(offer.slug);
  const maxAdd = Math.max(0, (offer.stock || 0) - inCart);
  const addQty = Math.min(qty, Math.max(1, maxAdd));
  const tier = tierFor(offer, inCart > 0 ? inCart : addQty);
  const mediaLeft = offer.media_position === 'left';
  const [mediaIdx, setMediaIdx] = useState(0);

  const add = () => { cart.add(offer.slug, addQty, offer.stock); setQty(1); onAdded && onAdded(offer, addQty, tier.price_ht); };

  const items = galleryItems(offer);
  const t1 = offer.tiers[0];
  return (
    <article id={`offre-${offer.slug}`} data-testid={`promo-offer-${offer.slug}`} data-offer-slug={offer.slug}
      style={{ animationDelay: `${Math.min(index, 4) * 90}ms` }}
      className={`promo-card-in scroll-mt-40 bg-[#0F0F10] rounded-3xl overflow-hidden border transition-[border-color,box-shadow] duration-300 ${highlighted ? 'border-[#C9A84C]/70 shadow-[0_0_0_1px_rgba(201,168,76,0.35),0_30px_80px_-40px_rgba(201,168,76,0.45)]' : 'border-white/[0.07]'}`}>
      <div className="grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className={`${mediaLeft ? 'md:order-1' : 'md:order-2'} bg-[#0A0A0A] pb-3 md:pb-4 md:sticky md:top-24 md:self-start`}>
          <div className="relative overflow-hidden h-[270px] md:h-[440px]" data-testid={`offer-media-${offer.slug}`}>
            <OfferMedia offer={offer} eager={eager} idx={mediaIdx} setIdx={setMediaIdx} />
            {offer.reference_price_ht && t1 && (
              <span className="absolute top-3 left-3 bg-[#C9A84C] text-black text-xs font-bold px-3 py-1.5 rounded-full pointer-events-none" data-testid={`offer-discount-${offer.slug}`}>
                -{Math.round((1 - t1.price_ht / offer.reference_price_ht) * 100)} %
              </span>
            )}
            <span className="absolute top-3 right-3 border border-[#C9A84C] text-[#E6CF8A] bg-black/60 text-xs font-medium px-3 py-1.5 rounded-full pointer-events-none" data-testid={`offer-free-shipping-${offer.slug}`}>Livraison gratuite</span>
          </div>
          {items.length > 1 && <Thumbs offer={offer} items={items} idx={mediaIdx} setIdx={setMediaIdx} />}
        </div>
        <div className={`p-4 sm:p-5 flex flex-col gap-3 ${mediaLeft ? 'md:order-2' : 'md:order-1'}`}>
          <div className="space-y-1">
            <h2 className="text-xl md:text-2xl font-semibold text-[#F2EFE8] leading-tight tracking-tight" data-testid={`offer-name-${offer.slug}`}>{offer.name}</h2>
            {offer.subtitle && <p className="text-sm text-zinc-400" data-testid={`offer-subtitle-${offer.slug}`}>{offer.subtitle}</p>}
          </div>
          <UnitPrice offer={offer} />
          {!offer.sold_out && isLowStock(offer) && (
            <span className="text-xs text-orange-300 flex items-center gap-1" data-testid={`offer-low-stock-${offer.slug}`}><AlertTriangle className="w-3.5 h-3.5" /> Plus que {offer.stock} pièce{offer.stock > 1 ? 's' : ''}</span>
          )}
          {offer.tiers.length > 1 && <TierPills offer={offer} activeMin={tier.min} />}
          <KeySpecs offer={offer} />
          {!offer.sold_out && offer.end_date && <Countdown endDate={offer.end_date} slug={offer.slug} />}
          <OfferTypeInfo offer={offer} />
          {offer.sold_out ? (
            <div className="bg-red-500/10 border border-red-500/40 text-red-300 rounded-xl h-11 flex items-center justify-center font-semibold" data-testid={`offer-sold-out-${offer.slug}`}>Épuisé</div>
          ) : inCart > 0 ? (
            <div className="space-y-1.5" data-testid={`offer-in-cart-${offer.slug}`}>
              <p className="text-sm text-[#E6CF8A] font-medium tabular-nums" data-testid={`offer-in-cart-qty-${offer.slug}`}>
                Déjà {inCart} au panier · CHF {fmt(tier.price_ht)} HT / pce · CHF {fmt(tier.price_ttc)} TTC
              </p>
              <div className="flex gap-2">
                <button onClick={onEdit} className="flex-1 h-11 bg-[#C9A84C] text-black rounded-xl font-semibold hover:bg-[#D8BA62] transition-colors flex items-center justify-center gap-2" data-testid={`offer-edit-qty-${offer.slug}`}>
                  <ShoppingCart className="w-4 h-4" /> Modifier la quantité
                </button>
                <button onClick={() => { if (window.confirm('Vider le panier des offres promo ?')) cart.clear(); }} className="h-11 px-4 rounded-xl border border-white/15 text-sm text-zinc-300 hover:text-red-300 hover:border-red-400/50 transition-colors" data-testid={`offer-clear-cart-${offer.slug}`}>
                  Vider le panier
                </button>
              </div>
              <SavingHint offer={offer} totalQty={inCart} slug={offer.slug} />
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex gap-2.5 items-center">
                <QtySelector value={addQty} onChange={setQty} max={maxAdd} testid={`offer-qty-${offer.slug}`} />
                <button onClick={add} disabled={maxAdd < 1} className="flex-1 h-11 bg-[#C9A84C] text-black rounded-xl font-semibold hover:bg-[#D8BA62] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-40 flex items-center justify-center gap-2" data-testid={`offer-add-to-cart-${offer.slug}`}>
                  <ShoppingCart className="w-4 h-4" /> Ajouter au panier
                </button>
              </div>
              <p className="text-xs text-zinc-400 tabular-nums" data-testid={`offer-current-price-${offer.slug}`}>
                {inCart + addQty} pce{inCart + addQty > 1 ? 's' : ''} : CHF {fmt(tier.price_ht)} HT / pce · CHF {fmt(tier.price_ttc)} TTC
                {inCart > 0 && <span className={GOLD}> · {inCart} déjà au panier</span>}
              </p>
              <SavingHint offer={offer} totalQty={inCart + addQty} slug={offer.slug} />
            </div>
          )}
          <DatasheetButton offer={offer} />
          <TrustRow slug={offer.slug} />
          <OfferDocuments offer={offer} skipFirstDatasheet />
          <div className="flex items-center gap-4 flex-wrap">
            <QuestionLink subject={`Question sur l'offre : ${offer.name}`} testid={`offer-question-${offer.slug}`} />
            {onRequest && <button type="button" onClick={() => onRequest(offer)} className="min-h-[44px] md:min-h-0 text-sm text-[#C9A84C] underline underline-offset-4" data-testid={`offer-request-${offer.slug}`}>Demander une offre</button>}
          </div>
        </div>
      </div>
    </article>
  );
};
